import React, { useState, useEffect } from 'react';
import './AdminPage.css';
import PlayerDashboard from './PlayerDashboard';
import { buildApiUrl } from '../config/api';

interface AdminPageProps {
  onBackToLanding: () => void;
}

interface User {
  id: number;
  user_id: number;
  username: string;
  email: string;
  password?: string;
  first_name: string;
  last_name: string;
  [key: string]: any;
}

interface PerformanceQueriesSectionProps {
  onBack: () => void;
}

const PerformanceQueriesSection: React.FC<PerformanceQueriesSectionProps> = ({ onBack }) => {
  const [query, setQuery] = useState('');
  const [dataSource, setDataSource] = useState<'players' | 'coaches' | 'parents' | null>(null);
  const [response, setResponse] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const getToken = (): string | null => {
    return localStorage.getItem('access_token');
  };

  const handleSubmit = async () => {
    if (!query.trim()) {
      alert('Please enter a query');
      return;
    }

    const token = getToken();
    if (!token) return;

    setLoading(true);
    setResponse(null);

    try {
      
      const response = await fetch(buildApiUrl('/admin/performance-query'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query: query,
          data_source: dataSource
        })
      });

      const data = await response.json();
      if (response.ok) {
        setResponse(data.response || 'No response');
      } else {
        setResponse(`Error: ${data.detail || 'Query failed'}`);
      }
    } catch (err: any) {
      setResponse(`Error: ${err.message || 'Query failed'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="queries-section">
      <h2>Performance Queries</h2>
      <p>Ask questions about player, coach, or parent data using AI.</p>
      
      <div className="form-group" style={{ marginBottom: '1rem' }}>
        <label>Query</label>
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g., Which player has the highest score in Leadership?"
          rows={4}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #383b48', backgroundColor: '#11121b', color: '#fdfeff' }}
        />
      </div>

      <div className="form-group" style={{ marginBottom: '1rem' }}>
        <label>Include Data Source (Optional)</label>
        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="radio"
              name="dataSource"
              checked={dataSource === 'players'}
              onChange={() => setDataSource('players')}
            />
            Players
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="radio"
              name="dataSource"
              checked={dataSource === 'coaches'}
              onChange={() => setDataSource('coaches')}
            />
            Coaches
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="radio"
              name="dataSource"
              checked={dataSource === 'parents'}
              onChange={() => setDataSource('parents')}
            />
            Parents
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="radio"
              name="dataSource"
              checked={dataSource === null}
              onChange={() => setDataSource(null)}
            />
            None
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
        <button onClick={handleSubmit} disabled={loading} className="button primary">
          {loading ? 'Querying...' : 'Submit Query'}
        </button>
        {response && (
          <button 
            onClick={() => {
              setQuery('');
              setResponse(null);
              setDataSource(null);
            }} 
            className="button"
            style={{ backgroundColor: '#383b48', color: '#fdfeff' }}
          >
            Clear Query
          </button>
        )}
        <button 
          onClick={onBack} 
          className="button"
          style={{ backgroundColor: '#383b48', color: '#fdfeff' }}
        >
          Back to Admin
        </button>
      </div>

      {response && (
        <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#1f2230', borderRadius: '8px', border: '1px solid #383b48' }}>
          <h3>Response:</h3>
          <p style={{ whiteSpace: 'pre-wrap' }}>{response}</p>
        </div>
      )}
    </div>
  );
};

const IndicatorWeightsSection: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [weights, setWeights] = useState<{
    indicatorWeights: {
      technical: { [key: string]: number };
      tactical: { [key: string]: number };
      physical: { [key: string]: number };
      psychoSocial: { [key: string]: number };
    };
    categoryWeights: {
      technical: number;
      tactical: number;
      physical: number;
      psychoSocial: number;
    };
  }>({
    indicatorWeights: {
      technical: {
        first_touch: 0.2,
        passing_accuracy: 0.2,
        ball_control: 0.2,
        use_of_both_feet: 0.2,
        shooting: 0.2,
      },
      tactical: {
        field_vision: 0.2,
        decision_making: 0.2,
        positioning: 0.2,
        off_the_ball_movement: 0.2,
        tackling: 0.2,
      },
      physical: {
        speed: 0.25,
        strength: 0.25,
        endurance: 0.25,
        change_of_pace: 0.25,
      },
      psychoSocial: {
        resilience: 0.2,
        communication: 0.2,
        leadership: 0.2,
        concentration: 0.2,
        composure: 0.2,
      },
    },
    categoryWeights: {
      technical: 0.30,
      tactical: 0.25,
      physical: 0.25,
      psychoSocial: 0.20,
    },
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const getToken = (): string | null => {
    return localStorage.getItem('access_token');
  };

  useEffect(() => {
    loadWeights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadWeights = async () => {
    const token = getToken();
    if (!token) return;

    setLoading(true);
    try {
      
      const response = await fetch(buildApiUrl('/admin/indicator-weights'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.weights) {
        // Handle both old and new format
        if (data.weights.indicatorWeights && data.weights.categoryWeights) {
          setWeights(data.weights);
        } else {
          // Old format - convert to new format
          const oldWeights = data.weights;
          setWeights({
            indicatorWeights: {
              technical: {
                first_touch: 0.2,
                passing_accuracy: 0.2,
                ball_control: 0.2,
                use_of_both_feet: 0.2,
                shooting: 0.2,
              },
              tactical: {
                field_vision: 0.2,
                decision_making: 0.2,
                positioning: 0.2,
                off_the_ball_movement: 0.2,
                tackling: 0.2,
              },
              physical: {
                speed: 0.25,
                strength: 0.25,
                endurance: 0.25,
                change_of_pace: 0.25,
              },
              psychoSocial: {
                resilience: 0.2,
                communication: 0.2,
                leadership: 0.2,
                concentration: 0.2,
                composure: 0.2,
              },
            },
            categoryWeights: {
              technical: oldWeights.Technical || oldWeights.technical || 0.30,
              tactical: oldWeights.Tactical || oldWeights.tactical || 0.25,
              physical: oldWeights.Physical || oldWeights.physical || 0.25,
              psychoSocial: oldWeights['Psycho-Social'] || oldWeights.psychoSocial || 0.20,
            },
          });
        }
      }
    } catch (err) {
      console.error('Error loading weights:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleIndicatorWeightChange = (category: string, indicator: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0 || numValue > 1) return;
    
    setWeights(prev => ({
      ...prev,
      indicatorWeights: {
        ...prev.indicatorWeights,
        [category as keyof typeof prev.indicatorWeights]: {
          ...prev.indicatorWeights[category as keyof typeof prev.indicatorWeights],
          [indicator]: numValue
        }
      }
    }));
    setError(null);
  };

  const handleCategoryWeightChange = (category: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0 || numValue > 1) return;
    
    setWeights(prev => ({
      ...prev,
      categoryWeights: {
        ...prev.categoryWeights,
        [category]: numValue
      }
    }));
    setError(null);
  };

  const calculateCategoryTotal = (category: string) => {
    const indicators = weights.indicatorWeights[category as keyof typeof weights.indicatorWeights];
    return Object.values(indicators).reduce((sum, val) => sum + val, 0);
  };

  const calculateCategoryWeightsTotal = () => {
    return Object.values(weights.categoryWeights).reduce((sum, val) => sum + val, 0);
  };

  const handleSave = async () => {
    // Validate indicator weights sum to 1.0 for each category
    const categories = ['technical', 'tactical', 'physical', 'psychoSocial'];
    for (const cat of categories) {
      const total = calculateCategoryTotal(cat);
      if (Math.abs(total - 1.0) > 0.01) {
        setError(`${cat.charAt(0).toUpperCase() + cat.slice(1)} indicator weights must sum to 100% (currently ${(total * 100).toFixed(2)}%)`);
        return;
      }
    }

    // Validate category weights sum to 1.0
    const categoryTotal = calculateCategoryWeightsTotal();
    if (Math.abs(categoryTotal - 1.0) > 0.01) {
      setError(`Category weights must sum to 100% (currently ${(categoryTotal * 100).toFixed(2)}%)`);
      return;
    }

    const token = getToken();
    if (!token) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      
      const response = await fetch(buildApiUrl('/admin/indicator-weights'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          weights: weights,
          recalculate: true
        })
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || 'Weights updated and all evaluations recalculated successfully');
        setIsEditing(false);
      } else {
        setError(data.detail || 'Failed to update weights');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update weights');
    } finally {
      setLoading(false);
    }
  };

  const categoryLabels: { [key: string]: string } = {
    technical: 'Technical',
    tactical: 'Tactical',
    physical: 'Physical',
    psychoSocial: 'Psycho-Social',
  };

  const indicatorLabels: { [key: string]: string } = {
    first_touch: 'First Touch',
    passing_accuracy: 'Passing Accuracy',
    ball_control: 'Ball Control',
    use_of_both_feet: 'Use of Both Feet',
    shooting: 'Shooting',
    field_vision: 'Field Vision',
    decision_making: 'Decision Making',
    positioning: 'Positioning',
    off_the_ball_movement: 'Off the Ball Movement',
    tackling: 'Tackling',
    speed: 'Speed',
    strength: 'Strength',
    endurance: 'Endurance',
    change_of_pace: 'Change of Pace',
    resilience: 'Resilience',
    communication: 'Communication',
    leadership: 'Leadership',
    concentration: 'Concentration',
    composure: 'Composure',
  };

  if (loading && !weights.indicatorWeights.technical.first_touch) {
    return (
      <div className="queries-section">
        <h2>Manage Indicator Weights</h2>
        <p>Loading weights...</p>
      </div>
    );
  }

  return (
    <div className="queries-section" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
      <h2>Manage Indicator Weights</h2>
      <p>Configure indicator weights within each category and category weights. Each category's indicator weights must sum to 100%, and category weights must sum to 100%.</p>

      {error && (
        <div style={{ 
          marginBottom: '1rem', 
          padding: '0.75rem', 
          backgroundColor: '#ef4444', 
          color: '#fff', 
          borderRadius: '6px' 
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ 
          marginBottom: '1rem', 
          padding: '0.75rem', 
          backgroundColor: '#4ade80', 
          color: '#fff', 
          borderRadius: '6px' 
        }}>
          {success}
        </div>
      )}

      {/* Indicator Weights by Category */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#567ad8', marginBottom: '1rem' }}>Indicator Weights (within each category)</h3>
        {Object.entries(weights.indicatorWeights).map(([category, indicators]) => {
          const categoryTotal = calculateCategoryTotal(category);
          const isValidCategory = Math.abs(categoryTotal - 1.0) <= 0.01;
          
          return (
            <div key={category} style={{ 
              marginBottom: '2rem', 
              padding: '1rem', 
              backgroundColor: '#1f2230', 
              borderRadius: '8px',
              border: '1px solid #383b48'
            }}>
              <h4 style={{ color: '#567ad8', marginBottom: '0.75rem' }}>
                {categoryLabels[category]}
              </h4>
              {Object.entries(indicators).map(([indicator, value]) => (
                <div key={indicator} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem', 
                  marginBottom: '0.5rem' 
                }}>
                  <label style={{ 
                    flex: '1', 
                    color: '#e0e0e0',
                    minWidth: '200px'
                  }}>
                    {indicatorLabels[indicator] || indicator.replace(/_/g, ' ')}:
                  </label>
                  {isEditing ? (
                    <>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.0001"
                        value={value}
                        onChange={(e) => handleIndicatorWeightChange(category, indicator, e.target.value)}
                        style={{
                          width: '120px',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid #383b48',
                          backgroundColor: '#11121b',
                          color: '#fdfeff'
                        }}
                      />
                      <span style={{ color: '#e0e0e0', minWidth: '60px' }}>
                        {(value * 100).toFixed(2)}%
                      </span>
                    </>
                  ) : (
                    <span style={{ color: '#e0e0e0' }}>
                      {(value * 100).toFixed(2)}%
                    </span>
                  )}
                </div>
              ))}
              <div style={{ 
                marginTop: '0.75rem', 
                padding: '0.5rem', 
                backgroundColor: '#11121b', 
                borderRadius: '4px',
                border: `1px solid ${isValidCategory ? '#4ade80' : '#ef4444'}`
              }}>
                <strong style={{ color: isValidCategory ? '#4ade80' : '#ef4444' }}>
                  Category Total: {(categoryTotal * 100).toFixed(2)}%
                  {!isValidCategory && ' (Must equal 100%)'}
                </strong>
              </div>
            </div>
          );
        })}
      </div>

      {/* Category Weights */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#567ad8', marginBottom: '1rem' }}>Category Weights</h3>
        {Object.entries(weights.categoryWeights).map(([category, value]) => (
          <div key={category} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem', 
            marginBottom: '0.75rem',
            padding: '0.75rem',
            backgroundColor: '#1f2230',
            borderRadius: '6px',
            border: '1px solid #383b48'
          }}>
            <label style={{ 
              flex: '1', 
              color: '#e0e0e0',
              fontWeight: 'bold'
            }}>
              {categoryLabels[category]}:
            </label>
            {isEditing ? (
              <>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.0001"
                  value={value}
                  onChange={(e) => handleCategoryWeightChange(category, e.target.value)}
                  style={{
                    width: '120px',
                    padding: '0.5rem',
                    borderRadius: '6px',
                    border: '1px solid #383b48',
                    backgroundColor: '#11121b',
                    color: '#fdfeff'
                  }}
                />
                <span style={{ color: '#e0e0e0', minWidth: '60px' }}>
                  {(value * 100).toFixed(2)}%
                </span>
              </>
            ) : (
              <span style={{ color: '#e0e0e0' }}>
                {(value * 100).toFixed(2)}%
              </span>
            )}
          </div>
        ))}
        <div style={{ 
          marginTop: '1rem', 
          padding: '1rem', 
          backgroundColor: '#1f2230', 
          borderRadius: '6px',
          border: '1px solid #383b48'
        }}>
          <strong style={{ 
            color: Math.abs(calculateCategoryWeightsTotal() - 1.0) <= 0.01 ? '#4ade80' : '#ef4444' 
          }}>
            Category Weights Total: {(calculateCategoryWeightsTotal() * 100).toFixed(2)}%
            {Math.abs(calculateCategoryWeightsTotal() - 1.0) > 0.01 && ' (Must equal 100%)'}
          </strong>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        {!isEditing ? (
          <>
            <button 
              onClick={() => setIsEditing(true)} 
              className="button primary"
            >
              Edit
            </button>
            <button 
              onClick={onBack} 
              className="button"
              style={{ backgroundColor: '#383b48', color: '#fdfeff' }}
            >
              Exit
            </button>
          </>
        ) : (
          <>
            <button 
              onClick={handleSave} 
              className="button primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save & Recalculate All Evaluations'}
            </button>
            <button 
              onClick={() => {
                setIsEditing(false);
                setError(null);
                loadWeights();
              }} 
              className="button"
              style={{ backgroundColor: '#383b48', color: '#fdfeff' }}
              disabled={loading}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Shared team options list - used for both players and coaches
const TEAM_OPTIONS = [
  'Skilz School',
  'U12 A',
  'U12 B',
  'U13 A',
  'U13 B',
  'U14 A',
  'U14 B',
  'U15 A',
  'U15 B',
  'U16 A',
  'U16 B',
  'U17 A',
  'U17 B',
  'U18 A',
  'U18 B',
  'U18',
  'Seniors'
];

const AdminPage: React.FC<AdminPageProps> = ({ onBackToLanding }) => {
  const [currentSection, setCurrentSection] = useState<'coaches' | 'players' | 'parents' | 'admins' | 'api' | 'queries' | 'weights' | 'search'>('coaches');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // const [editingChildren, setEditingChildren] = useState(false); // Unused - keeping for future use
  const [addingChild, setAddingChild] = useState(false);
  const [newChildData, setNewChildData] = useState<{player_id?: number | null, player_full_name?: string, relationship_type?: string}>({});
  const [allPlayers, setAllPlayers] = useState<User[]>([]);
  const [coaches, setCoaches] = useState<User[]>([]);
  const [players, setPlayers] = useState<User[]>([]);
  const [parents, setParents] = useState<User[]>([]);
  const [admins, setAdmins] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const [apiKey, setApiKey] = useState('');
  const [apiKeyActive, setApiKeyActive] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addFormData, setAddFormData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [viewingPlayerDashboard, setViewingPlayerDashboard] = useState(false);
  const [playerHasEvaluation, setPlayerHasEvaluation] = useState<boolean | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (currentSection === 'coaches') {
      loadCoaches();
    } else if (currentSection === 'players') {
      loadPlayers();
    } else if (currentSection === 'parents') {
      loadParents();
    } else if (currentSection === 'admins') {
      loadAdmins();
    } else if (currentSection === 'api') {
      loadApiKey();
    }
    // Clear selections when switching sections
    setSelectedUserIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection]);

  // Load all players when viewing a parent (for child selection)
  useEffect(() => {
    if (selectedUser && currentSection === 'parents') {
      loadAllPlayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, currentSection]);

  const getToken = (): string | null => {
    return localStorage.getItem('access_token');
  };

  const loadAllPlayers = async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      const response = await fetch(buildApiUrl('/admin/players'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAllPlayers(data.players || []);
    } catch (err) {
      console.error('Error loading players:', err);
    }
  };

  const loadCoaches = async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      const response = await fetch(buildApiUrl('/admin/coaches'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCoaches(data.coaches || []);
    } catch (err) {
      console.error('Error loading coaches:', err);
    }
  };

  const loadPlayers = async () => {
    const token = getToken();
    if (!token) {
      console.error('No token found for loading players');
      // Don't show alert here - just log it, as token might be temporarily unavailable
      // Alert will be shown when user tries to perform an action that requires auth
      return;
    }
    
    try {
      const apiUrl = buildApiUrl('/admin/players');
      console.log('Loading players from:', apiUrl);
      console.log('Token exists:', !!token);
      
      const response = await fetch(apiUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to load players:', response.status, errorText);
        let errorMessage = `Failed to load players (Status: ${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage += `: ${errorData.detail || errorText}`;
        } catch {
          errorMessage += `: ${errorText}`;
        }
        console.error('Error details:', errorMessage);
        setPlayers([]);
        return;
      }
      
      const data = await response.json();
      console.log('Players data received:', data);
      console.log('Number of players:', data.players ? data.players.length : 0);
      setPlayers(data.players || []);
      
      if (!data.players || data.players.length === 0) {
        console.warn('No players returned from API. Response:', data);
      }
    } catch (err) {
      console.error('Error loading players:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Full error:', err);
      setPlayers([]);
      // Don't show alert for network errors as they might be expected in dev
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        console.warn('Network error - this might be expected if backend is not running');
      } else {
        alert(`Error loading players: ${errorMessage}`);
      }
    }
  };

  const loadParents = async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      const response = await fetch(buildApiUrl('/admin/parents'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Ensure parents have name fields
      const parentsWithNames = (data.parents || []).map((parent: any) => {
        // Create a name field if it doesn't exist
        if (!parent.name && (parent.first_name || parent.last_name)) {
          parent.name = `${parent.first_name || ''} ${parent.last_name || ''}`.trim();
        }
        return parent;
      });
      
      setParents(parentsWithNames);
    } catch (err) {
      console.error('Error loading parents:', err);
      setParents([]);
    }
  };

  const loadAdmins = async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      const response = await fetch(buildApiUrl('/admin/admins'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Ensure admins have name fields
      const adminsWithNames = (data.admins || []).map((admin: any) => {
        // Create a name field if it doesn't exist
        if (!admin.name && (admin.first_name || admin.last_name)) {
          admin.name = `${admin.first_name || ''} ${admin.last_name || ''}`.trim();
        }
        return admin;
      });
      
      setAdmins(adminsWithNames);
    } catch (err) {
      console.error('Error loading admins:', err);
      setAdmins([]);
    }
  };

  const loadApiKey = async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      
      const response = await fetch(buildApiUrl('/admin/api-key'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setApiKeyActive(data.enabled || false);
    } catch (err) {
      console.error('Error loading API key status:', err);
    }
  };

  const handleAdminSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      return;
    }

    const token = getToken();
    if (!token) {
      alert('Authentication required. Please log in again.');
      return;
    }

    setSearchLoading(true);
    try {
      
      const response = await fetch(
        buildApiUrl(`/admin/search?query=${encodeURIComponent(searchQuery)}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Search failed: ${errorData.detail || 'Unknown error'}`);
        setSearchResults([]);
      }
    } catch (err: any) {
      console.error('Search error:', err);
      alert('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddChild = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🔵 handleAddChild called', { 
      selectedUser: selectedUser?.id, 
      currentSection, 
      newChildData,
      addingChild 
    });
    
    if (!selectedUser) {
      console.error('❌ No selectedUser');
      alert('Error: No parent selected. Please select a parent first.');
      return;
    }
    
    if (currentSection !== 'parents') {
      console.error('❌ Wrong section:', currentSection);
      alert(`Error: Current section is "${currentSection}", expected "parents"`);
      return;
    }
    
    const token = getToken();
    if (!token) {
      console.error('❌ No token');
      alert('Authentication required. Please log in again.');
      return;
    }

    if (!newChildData.player_id && !newChildData.player_full_name?.trim()) {
      console.error('❌ No player_id or player_full_name');
      alert('Please select a player or enter a child name');
      return;
    }

    console.log('✅ All checks passed, making API call...');

    try {
      
      const requestBody = {
        player_id: newChildData.player_id || null,
        player_full_name: newChildData.player_full_name?.trim() || null,
        relationship_type: newChildData.relationship_type || 'Child'
      };
      
      const childUrl = buildApiUrl(`/admin/parents/${selectedUser.id}/children`);
      console.log('📤 Adding child with data:', {
        url: childUrl,
        parent_id: selectedUser.id,
        body: requestBody
      });
      
      const response = await fetch(
        childUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        }
      );

      const responseData = await response.json().catch(() => ({}));
      console.log('📥 Add child response:', { 
        status: response.status, 
        ok: response.ok, 
        data: responseData 
      });

      if (response.ok) {
        console.log('✅ Child added successfully!');
        alert('Child added successfully!');
        // Reload parent details to refresh children list
        await handleUserClick(selectedUser.id, 'parents');
        setAddingChild(false);
        setNewChildData({ relationship_type: 'Child' });
      } else {
        const errorMsg = responseData.detail || responseData.message || `HTTP ${response.status}: ${response.statusText}`;
        console.error('❌ Failed to add child:', errorMsg);
        alert(`Failed to add child: ${errorMsg}`);
      }
    } catch (err: any) {
      console.error('❌ Error adding child:', err);
      alert(`Failed to add child: ${err.message || 'Please try again.'}`);
    }
  };

  const handleUpdateChild = async (parentId: number, child: any, updates: any) => {
    const token = getToken();
    if (!token) return;

    // child_id should be included in the child object from backend
    const childId = child.child_id;
    if (!childId) {
      console.error('Child ID not found in child object:', child);
      alert('Error: Child ID not found. Please refresh the page and try again.');
      return;
    }

    try {
      
      const response = await fetch(
        buildApiUrl(`/admin/parents/${parentId}/children/${childId}`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(updates)
        }
      );

      if (response.ok) {
        // Reload parent details to refresh children list
        await handleUserClick(parentId, 'parents');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to update child: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err: any) {
      console.error('Error updating child:', err);
      alert('Failed to update child. Please try again.');
    }
  };

  const handleDeleteChild = async (parentId: number, child: any) => {
    if (!window.confirm(`Are you sure you want to remove ${child.player_name} from this parent?`)) {
      return;
    }

    const token = getToken();
    if (!token) return;

    const childId = child.child_id;
    if (!childId) {
      console.error('Child ID not found in child object:', child);
      alert('Error: Child ID not found. Please refresh the page and try again.');
      return;
    }

    try {
      
      const response = await fetch(
        buildApiUrl(`/admin/parents/${parentId}/children/${childId}`),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        // Reload parent details to refresh children list
        await handleUserClick(parentId, 'parents');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to remove child: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err: any) {
      console.error('Error deleting child:', err);
      alert('Failed to remove child. Please try again.');
    }
  };

  const checkPlayerHasEvaluation = async (playerId: number): Promise<boolean> => {
    const token = getToken();
    if (!token) return false;
    
    try {
      
      const response = await fetch(buildApiUrl(`/evaluations/player/${playerId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Only clear token on 401 if error explicitly indicates token expiration
      if (response.status === 401) {
        try {
          const errorData = await response.json().catch(() => ({}));
          const errorDetail = (errorData.detail || '').toLowerCase();
          // Only clear token if error explicitly mentions token expiration (not just any mention of "token" or "authentication")
          // This prevents clearing token on generic auth errors that aren't about expiration
          const isExpiredError = errorDetail.includes('expired') || 
              errorDetail.includes('token expired') ||
              errorDetail.includes('jwt expired') ||
              (errorDetail.includes('invalid token') && errorDetail.includes('expired'));
          if (isExpiredError) {
            console.log('Token expired detected, clearing token');
            localStorage.removeItem('access_token');
            localStorage.removeItem('user_id');
            localStorage.removeItem('user_role');
          } else {
            console.warn('401 error but not token expiration:', errorDetail);
          }
        } catch (e) {
          // If can't parse error, don't clear token - might be a different issue
          console.warn('Could not parse 401 error response:', e);
        }
        return false;
      }
      
      return response.ok;
    } catch (err) {
      console.error('Error checking player evaluation:', err);
      return false;
    }
  };

  const handleUserClick = async (userId: number, type: 'coaches' | 'players' | 'parents') => {
    const token = getToken();
    if (!token) {
      console.error('[handleUserClick] No token found. Token was cleared:', !localStorage.getItem('access_token'));
      alert('Authentication required. Please log in again.');
      return;
    }
    console.log('[handleUserClick] Token exists, loading user:', { userId, type });
    
    try {
      
      const endpoint = buildApiUrl(`/admin/${type}/${userId}`);
      console.log('Loading user details from:', endpoint);
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Try to parse response as JSON
      let data;
      try {
        const text = await response.text();
        // Check if response is HTML (error page) instead of JSON
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          console.error('Non-JSON response received (HTML):', text.substring(0, 200));
          // Don't clear token on HTML responses - this is a server/routing error, not auth error
          throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}. This usually means the endpoint doesn't exist or there's a server error.`);
        }
        data = JSON.parse(text);
      } catch (parseError: any) {
        // If JSON parse fails and it's not HTML, it might be empty or malformed
        if (parseError.message && parseError.message.includes('HTML')) {
          throw parseError; // Re-throw HTML error
        }
        console.error('Failed to parse JSON response:', parseError);
        throw new Error(`Invalid response from server. Status: ${response.status}`);
      }
      
      if (!response.ok) {
        const errorDetail = (data.detail || '').toLowerCase();
        console.log('[handleUserClick] Error response:', { 
          status: response.status, 
          detail: data.detail, 
          errorDetail,
          hasExpired: errorDetail.includes('expired'),
          hasTokenExpired: errorDetail.includes('token expired'),
          hasJwtExpired: errorDetail.includes('jwt expired')
        });
        
        // Only clear token if 401 AND error explicitly indicates token expiration
        if (response.status === 401) {
          // Only clear token if error explicitly mentions token expiration (not just any mention of "token" or "authentication")
          // This prevents clearing token on generic auth errors that aren't about expiration
          if (errorDetail.includes('expired') || 
              errorDetail.includes('token expired') ||
              errorDetail.includes('jwt expired') ||
              (errorDetail.includes('invalid token') && errorDetail.includes('expired'))) {
            console.log('[handleUserClick] ⚠️ Token expired detected, clearing token. Error:', data.detail);
            localStorage.removeItem('access_token');
            localStorage.removeItem('user_id');
            localStorage.removeItem('user_role');
            alert('Your session has expired. Please log in again.');
            return;
          }
          // If 401 but not token expiration, just show error without clearing token
          console.warn('[handleUserClick] ⚠️ 401 error but NOT token expiration. Error:', data.detail || 'Unknown error');
          console.warn('[handleUserClick] Token will NOT be cleared. Current token:', localStorage.getItem('access_token') ? 'EXISTS' : 'MISSING');
          throw new Error(data.detail || `Authentication failed: ${response.status} ${response.statusText}`);
        }
        // For other errors (404, 500, etc.), don't clear token
        console.log('[handleUserClick] Non-401 error, token NOT cleared:', response.status);
        throw new Error(data.detail || `Failed to load user details: ${response.status} ${response.statusText}`);
      }
      const userData = data[type.slice(0, -1)] || data; // Backend returns {coach: {...}} or {player: {...}}
      
      // Set default club for players if not set
      if (type === 'players' && !userData.club) {
        userData.club = 'Shosholoza FC';
      }
      
      setSelectedUser(userData);
      setEditData(userData);
      setIsEditing(false);
      setViewingPlayerDashboard(false);
      
      // Check if player has evaluation when viewing a player
      if (type === 'players') {
        const hasEval = await checkPlayerHasEvaluation(userId);
        setPlayerHasEvaluation(hasEval);
      } else {
        setPlayerHasEvaluation(null);
      }
    } catch (err: any) {
      console.error('Error loading user details:', err);
      const errorMessage = err.message || 'Error loading user details';
      alert(errorMessage);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    
    const token = getToken();
    if (!token) return;
    
    try {
      const type = currentSection === 'coaches' ? 'coaches' : currentSection === 'players' ? 'players' : 'parents';
      
      // Validate required fields
      if (type === 'players' && (!editData.team || editData.team.trim() === '')) {
        alert('⚠️ ERROR: Team is required for all players. Please select a team from the dropdown menu.');
        return;
      }
      
      
      const endpoint = buildApiUrl(`/admin/${type}/${selectedUser.id}`);
      
      // Filter out read-only fields and convert empty strings to null
      const updateData: any = {};
      Object.keys(editData).forEach(key => {
        if (key !== 'id' && key !== 'user_id' && key !== 'username') {
          const value = editData[key];
          // Include password if it's been changed (not empty and different from original)
          if (key === 'password') {
            if (value && value.trim() !== '' && value !== selectedUser.password) {
              updateData[key] = value;
            }
          } else if (key === 'teams' && type === 'coaches') {
            // Convert teams from comma-separated string to JSON string format
            // IMPORTANT: Send as a simple comma-separated string, NOT JSON stringified
            // The backend will handle the conversion
            if (value && typeof value === 'string') {
              const teams = value.split(',').map((t: string) => t.trim()).filter((t: string) => t);
              // Send as comma-separated string, not JSON stringified (to avoid double encoding)
              updateData[key] = teams.length > 0 ? teams.join(', ') : null;
            } else {
              updateData[key] = value === '' ? null : value;
            }
          } else {
            updateData[key] = value === '' ? null : value;
          }
        }
      });
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData),
      });
      
      if (response.ok) {
        alert('User updated successfully');
        setIsEditing(false);
        // Reload the list
        if (type === 'coaches') loadCoaches();
        else if (type === 'players') loadPlayers();
        else loadParents();
        // Reload the selected user
        handleUserClick(selectedUser.id, type);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to update user'}`);
      }
    } catch (err) {
      console.error('Error saving user:', err);
      alert('Error saving user');
    }
  };

  const handleToggleUserSelection = (userId: number) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (users: User[]) => {
    if (selectedUserIds.size === users.length) {
      // Deselect all
      setSelectedUserIds(new Set());
    } else {
      // Select all
      setSelectedUserIds(new Set(users.map(u => u.id)));
    }
  };

  const handleDeleteSelected = async (type: 'coaches' | 'players' | 'parents') => {
    if (selectedUserIds.size === 0) {
      alert('Please select at least one user to delete');
      return;
    }

    const userTypeName = type.slice(0, -1);
    const count = selectedUserIds.size;
    if (!window.confirm(`Are you sure you want to delete ${count} ${count === 1 ? userTypeName : userTypeName + 's'}?`)) {
      return;
    }

    const token = getToken();
    if (!token) {
      alert('Authentication required. Please log in again.');
      return;
    }

    
    const userIds = Array.from(selectedUserIds);
    let successCount = 0;
    let failCount = 0;
    const errors: string[] = [];

    // Delete users one by one
    for (const userId of userIds) {
      try {
        const endpoint = buildApiUrl(`/admin/${type}/${userId}`);
        console.log(`Deleting ${userTypeName} ${userId} from ${endpoint}`);
        
        const response = await fetch(endpoint, {
          method: 'DELETE',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log(`Response for ${userTypeName} ${userId}:`, response.status, response.statusText);

        if (response.ok) {
          successCount++;
          console.log(`Successfully deleted ${userTypeName} ${userId}`);
        } else {
          failCount++;
          let errorDetail = `HTTP ${response.status}`;
          try {
            const errorData = await response.json();
            errorDetail = errorData.detail || errorData.message || errorDetail;
            console.error(`Failed to delete ${userTypeName} ${userId}:`, errorData);
          } catch (e) {
            const text = await response.text();
            errorDetail = text || errorDetail;
            console.error(`Failed to delete ${userTypeName} ${userId}:`, text);
          }
          errors.push(`${userTypeName} ${userId}: ${errorDetail}`);
        }
      } catch (err: any) {
        failCount++;
        const errorMsg = err.message || 'Network error';
        console.error(`Error deleting ${userTypeName} ${userId}:`, err);
        errors.push(`${userTypeName} ${userId}: ${errorMsg}`);
      }
    }

    // Show results
    if (successCount > 0) {
      const message = `Successfully deleted ${successCount} ${successCount === 1 ? userTypeName : userTypeName + 's'}.${failCount > 0 ? `\n\nFailed to delete ${failCount}:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n...and ${errors.length - 5} more` : ''}` : ''}`;
      alert(message);
      // Reload the list
      if (type === 'coaches') loadCoaches();
      else if (type === 'players') loadPlayers();
      else loadParents();
      setSelectedUserIds(new Set());
      setSelectedUser(null);
    } else {
      const errorMessage = `Failed to delete all ${userTypeName}s.\n\nErrors:\n${errors.slice(0, 10).join('\n')}${errors.length > 10 ? `\n...and ${errors.length - 10} more` : ''}\n\nPlease check the browser console for more details.`;
      alert(errorMessage);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteUser = async (userId: number, type: 'coaches' | 'players' | 'parents') => {
    if (!window.confirm(`Are you sure you want to delete this ${type.slice(0, -1)}?`)) {
      return;
    }
    
    const token = getToken();
    if (!token) return;
    
    try {
      
      // Use specific endpoints for each type instead of generic /admin/users/{type}/{id}
      const endpoint = buildApiUrl(`/admin/${type}/${userId}`);
      console.log('Deleting user:', { endpoint, userId, type });
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Delete response status:', response.status);
      
      if (response.ok) {
        await response.json().catch(() => ({})); // Response data not needed
        alert(`${type.slice(0, -1).charAt(0).toUpperCase() + type.slice(1, -1)} deleted successfully`);
        // Reload the list
        if (type === 'coaches') loadCoaches();
        else if (type === 'players') loadPlayers();
        else loadParents();
        setSelectedUser(null);
      } else {
        let errorMessage = 'Failed to delete user';
        try {
          const error = await response.json();
          errorMessage = error.detail || error.message || errorMessage;
          console.error('Delete error response:', error);
        } catch (e) {
          const text = await response.text();
          errorMessage = text || `HTTP ${response.status}: ${response.statusText}`;
          console.error('Delete error (non-JSON):', text);
        }
        alert(`Error: ${errorMessage}`);
      }
    } catch (err: any) {
      console.error('Error deleting user:', err);
      alert(`Error deleting user: ${err.message || 'Network error'}`);
    }
  };

  const handleExit = () => {
    setSelectedUser(null);
    setIsEditing(false);
    setEditData({});
  };

  const handleSaveApiKey = async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      
      const response = await fetch(buildApiUrl('/admin/api-key'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ api_key: apiKey }),
      });
      
      if (response.ok) {
        alert('API key saved successfully');
        loadApiKey();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to save API key'}`);
      }
    } catch (err) {
      console.error('Error saving API key:', err);
      alert('Error saving API key');
    }
  };

  const handleToggleApiKey = async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      
      const response = await fetch(buildApiUrl('/admin/api-key/toggle'), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiKeyActive(data.enabled);
        alert(data.message);
      } else {
        alert('Error toggling API key');
      }
    } catch (err) {
      console.error('Error toggling API key:', err);
      alert('Error toggling API key');
    }
  };

  const handleRemoveAdmin = async (coachId: number) => {
    if (!window.confirm('Are you sure you want to remove admin status from this coach? They will remain as a regular coach.')) {
      return;
    }
    
    const token = getToken();
    if (!token) return;
    
    try {
      
      const response = await fetch(buildApiUrl(`/admin/admins/${coachId}/remove`), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Admin status removed successfully');
        loadAdmins(); // Reload the admins list
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to remove admin status'}`);
      }
    } catch (err) {
      console.error('Error removing admin status:', err);
      alert('Error removing admin status');
    }
  };

  const renderAdminsList = () => {
    return (
      <div className="user-list">
        <h2>Admins</h2>
        {admins.length === 0 ? (
          <p>No admins found</p>
        ) : (
          <div className="user-grid">
            {admins.map((admin) => (
              <div key={admin.id} className="user-card">
                <div className="user-info">
                  <h3>{admin.first_name} {admin.last_name}</h3>
                  <p>Username: {admin.username}</p>
                  <p>Email: {admin.email}</p>
                  <p style={{ color: '#567ad8', fontWeight: 'bold' }}>Admin</p>
                </div>
                <div className="user-actions">
                  <button
                    onClick={() => handleRemoveAdmin(admin.id)}
                    className="button danger"
                    style={{ marginTop: '0.5rem' }}
                  >
                    Remove Admin Status
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleAddUser = () => {
    setIsAdding(true);
    setAddFormData({});
    setFormErrors({});
    setSelectedUser(null);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setAddFormData({});
    setFormErrors({});
  };

  const getInputClassName = (fieldName: string): string => {
    return formErrors[fieldName] ? 'error' : '';
  };

  const getInputStyle = (fieldName: string) => {
    const hasError = !!formErrors[fieldName];
    const baseStyle: React.CSSProperties = {
      padding: '0.75rem',
      borderRadius: '5px',
      width: '100%',
      fontSize: '1rem',
      outline: 'none',
      transition: 'all 0.3s ease',
    };
    
    if (hasError) {
      return {
        ...baseStyle,
        border: '2px solid #ef4444',
        backgroundColor: '#2a1f1f',
        color: '#fdfeff',
        boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.3)',
      };
    }
    
    return {
      ...baseStyle,
      border: '1px solid #383b48',
      backgroundColor: '#11121b',
      color: '#fdfeff',
    };
  };

  const handleAddFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'last_name') {
      setAddFormData({ ...addFormData, [name]: value.toUpperCase() });
    } else if (name === 'assigned_teams') {
      // Handle teams as comma-separated string for now
      setAddFormData({ ...addFormData, [name]: value });
    } else {
      setAddFormData({ ...addFormData, [name]: value });
    }
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      const newErrors = { ...formErrors };
      delete newErrors[name];
      setFormErrors(newErrors);
    }
  };

  const validateForm = (type: 'coaches' | 'players' | 'parents'): boolean => {
    const errors: Record<string, string> = {};
    
    // Common required fields
    if (!addFormData.username) errors.username = 'Username is required';
    if (!addFormData.email) errors.email = 'Email is required';
    if (!addFormData.password) errors.password = 'Password is required';
    if (!addFormData.first_name) errors.first_name = 'First name is required';
    if (!addFormData.last_name) errors.last_name = 'Last name is required';
    
    if (type === 'coaches') {
      // Backend requires address and whatsapp_phone for coaches
      if (!addFormData.address) errors.address = 'Address is required';
      if (!addFormData.whatsapp_phone) errors.whatsapp_phone = 'WhatsApp phone is required';
    } else if (type === 'players') {
      if (!addFormData.date_of_birth) errors.date_of_birth = 'Date of birth is required';
      if (!addFormData.parent_whatsapp_phone) errors.parent_whatsapp_phone = 'Parent WhatsApp phone is required';
      if (!addFormData.address) errors.address = 'Address is required';
      if (!addFormData.parent_email) errors.parent_email = 'Parent email is required';
      if (!addFormData.team || addFormData.team.trim() === '') errors.team = 'Team is required for all players. Please select a team from the dropdown menu.';
    } else if (type === 'parents') {
      if (!addFormData.whatsapp_phone) errors.whatsapp_phone = 'WhatsApp phone is required';
      if (!addFormData.physical_address) errors.physical_address = 'Physical address is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitAdd = async (type: 'coaches' | 'players' | 'parents') => {
    // Validate form first
    if (!validateForm(type)) {
      alert('Please fill in all required fields (highlighted in red)');
      return; // Don't proceed if validation fails - errors are already set
    }
    
    const token = getToken();
    if (!token) return;

    setLoading(true);
    // Don't clear errors here - only clear them on successful submission
    try {
      
      const endpoint = buildApiUrl(`/auth/register/${type === 'coaches' ? 'coach' : type === 'players' ? 'player' : 'parent'}`);
      
      // Prepare data based on type
      let requestData: any = { ...addFormData };
      
      if (type === 'coaches') {
        // Parse teams from comma-separated string
        const teams = addFormData.assigned_teams ? addFormData.assigned_teams.split(',').map((t: string) => t.trim()).filter((t: string) => t) : [];
        requestData = {
          username: addFormData.username,
          email: addFormData.email,
          password: addFormData.password,
          first_name: addFormData.first_name,
          last_name: addFormData.last_name?.toUpperCase() || '',
          address: addFormData.address, // Required by backend
          whatsapp_phone: addFormData.whatsapp_phone, // Required by backend
          teams: teams.length > 0 ? teams.join(', ') : null,  // Send as comma-separated, not JSON
          licenses_certifications: addFormData.licenses_certifications || null,
          sa_id_number: addFormData.sa_id_number || null,
          safa_number: addFormData.safa_number || null,
          date_of_birth: addFormData.date_of_birth || null,
        };
      } else if (type === 'players') {
        requestData = {
          username: addFormData.username,
          email: addFormData.email,
          password: addFormData.password,
          first_name: addFormData.first_name,
          last_name: addFormData.last_name?.toUpperCase() || '',
          date_of_birth: addFormData.date_of_birth || null,
          gender: addFormData.gender || null,
          position: addFormData.position || null,
          footed: addFormData.footed || null,
          parent_contact_name: addFormData.parent_contact_name || null,
          parent_whatsapp_phone: addFormData.parent_whatsapp_phone || null,
          player_whatsapp_phone: addFormData.player_whatsapp_phone || null,
          address: addFormData.address || null,
          parent_email: addFormData.parent_email || null,
          player_email: addFormData.player_email || addFormData.email || null, // Use player_email if specified, otherwise use email
          previous_club: addFormData.previous_club || null,
          safa_id: addFormData.safa_id || null,
          sa_id_number: addFormData.sa_id_number || null,
          team: addFormData.team || null, // Team is required - validated above
        };
      } else if (type === 'parents') {
        requestData = {
          username: addFormData.username,
          email: addFormData.email,
          password: addFormData.password,
          first_name: addFormData.first_name,
          last_name: addFormData.last_name?.toUpperCase() || '',
          whatsapp_phone: addFormData.whatsapp_phone,
          physical_address: addFormData.physical_address,
          number_of_children: addFormData.number_of_children || 1,
          player_names: addFormData.player_names ? addFormData.player_names.split(',').map((n: string) => n.trim()).filter((n: string) => n) : [],
          relationship_type: addFormData.relationship_type || 'Parent',
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();
      if (response.ok) {
        alert(`${type.charAt(0).toUpperCase() + type.slice(1)} registered successfully!`);
        setIsAdding(false);
        setAddFormData({});
        setFormErrors({});
        // Reload the list
        if (type === 'coaches') loadCoaches();
        else if (type === 'players') loadPlayers();
        else if (type === 'parents') loadParents();
      } else {
        // Handle backend validation errors
        const backendErrors: Record<string, string> = {};
        if (data.detail) {
          if (typeof data.detail === 'string') {
            if (data.detail.includes('username') || data.detail.includes('email')) {
              backendErrors.username = data.detail;
              backendErrors.email = data.detail;
            } else {
              alert(`Registration failed: ${data.detail}`);
            }
          } else if (typeof data.detail === 'object') {
            Object.keys(data.detail).forEach(key => {
              backendErrors[key] = data.detail[key];
            });
          }
        }
        if (Object.keys(backendErrors).length > 0) {
          setFormErrors(backendErrors);
        } else {
          alert(`Registration failed: ${data.detail || 'Unknown error'}`);
        }
      }
    } catch (err: any) {
      alert(`Registration failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const renderAddForm = (type: 'coaches' | 'players' | 'parents') => {
    if (type === 'coaches') {
      return (
        <div className="add-form" style={{ background: '#1f2230', padding: '2rem', borderRadius: '8px', border: '1px solid #383b48' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#567ad8' }}>Add New Coach</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div className="detail-field">
              <label>Username *</label>
              <input 
                type="text" 
                name="username" 
                value={addFormData.username || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('username')}
                style={getInputStyle('username')}
              />
              {formErrors.username && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.username}</span>}
            </div>
            <div className="detail-field">
              <label>Email *</label>
              <input 
                type="email" 
                name="email" 
                value={addFormData.email || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('email')}
                style={getInputStyle('email')}
              />
              {formErrors.email && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.email}</span>}
            </div>
            <div className="detail-field">
              <label>Password *</label>
              <input 
                type="password" 
                name="password" 
                value={addFormData.password || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('password')}
                style={getInputStyle('password')}
              />
              {formErrors.password && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.password}</span>}
            </div>
            <div className="detail-field">
              <label>First Name *</label>
              <input 
                type="text" 
                name="first_name" 
                value={addFormData.first_name || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('first_name')}
                style={getInputStyle('first_name')}
              />
              {formErrors.first_name && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.first_name}</span>}
            </div>
            <div className="detail-field">
              <label>Last Name *</label>
              <input 
                type="text" 
                name="last_name" 
                value={addFormData.last_name || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('last_name')}
                style={getInputStyle('last_name')}
              />
              {formErrors.last_name && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.last_name}</span>}
            </div>
            <div className="detail-field">
              <label>Address *</label>
              <input 
                type="text" 
                name="address" 
                value={addFormData.address || ''} 
                onChange={handleAddFormChange} 
                required
                className={getInputClassName('address')}
                style={getInputStyle('address')}
              />
              {formErrors.address && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.address}</span>}
            </div>
            <div className="detail-field">
              <label>WhatsApp Phone *</label>
              <input 
                type="tel" 
                name="whatsapp_phone" 
                value={addFormData.whatsapp_phone || ''} 
                onChange={handleAddFormChange} 
                required
                className={getInputClassName('whatsapp_phone')}
                style={getInputStyle('whatsapp_phone')}
              />
              {formErrors.whatsapp_phone && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.whatsapp_phone}</span>}
            </div>
            <div className="detail-field">
              <label>Assigned Teams *</label>
              <select
                multiple
                name="assigned_teams" 
                value={addFormData.assigned_teams ? addFormData.assigned_teams.split(',').map((t: string) => t.trim()) : []} 
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                  setAddFormData({ ...addFormData, assigned_teams: selected.join(', ') });
                }}
                required
                style={{
                  ...getInputStyle('assigned_teams'),
                  minHeight: '120px',
                  padding: '0.5rem'
                }}
                size={Math.min(TEAM_OPTIONS.length, 8)}
              >
                {TEAM_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <span style={{ 
                color: '#797b84', 
                fontSize: '0.875rem', 
                marginTop: '0.5rem', 
                display: 'block' 
              }}>
                Hold Ctrl (Cmd on Mac) to select multiple teams
              </span>
              {formErrors.assigned_teams && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.assigned_teams}</span>}
            </div>
            <div className="detail-field">
              <label>Licenses/Certifications</label>
              <textarea 
                name="licenses_certifications" 
                value={addFormData.licenses_certifications || ''} 
                onChange={handleAddFormChange} 
                rows={3} 
                style={getInputStyle('licenses_certifications')}
              />
              {formErrors.licenses_certifications && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.licenses_certifications}</span>}
            </div>
            <div className="detail-field">
              <label>SA ID Number</label>
              <input 
                type="text" 
                name="sa_id_number" 
                value={addFormData.sa_id_number || ''} 
                onChange={handleAddFormChange} 
                style={getInputStyle('sa_id_number')}
              />
              {formErrors.sa_id_number && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.sa_id_number}</span>}
            </div>
            <div className="detail-field">
              <label>SAFA Number</label>
              <input 
                type="text" 
                name="safa_number" 
                value={addFormData.safa_number || ''} 
                onChange={handleAddFormChange} 
                style={getInputStyle('safa_number')}
              />
              {formErrors.safa_number && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.safa_number}</span>}
            </div>
            <div className="detail-field">
              <label>Date of Birth</label>
              <input 
                type="date" 
                name="date_of_birth" 
                value={addFormData.date_of_birth || ''} 
                onChange={handleAddFormChange} 
                style={getInputStyle('date_of_birth')}
              />
              {formErrors.date_of_birth && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.date_of_birth}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
            <button onClick={() => handleSubmitAdd('coaches')} className="save-btn" disabled={loading}>
              {loading ? 'Registering...' : 'Register Coach'}
            </button>
            <button onClick={handleCancelAdd} className="cancel-btn">Cancel</button>
          </div>
        </div>
      );
    } else if (type === 'players') {
      return (
        <div className="add-form" style={{ background: '#1f2230', padding: '2rem', borderRadius: '8px', border: '1px solid #383b48' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#567ad8' }}>Add New Player</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div className="detail-field">
              <label>Username *</label>
              <input 
                type="text" 
                name="username" 
                value={addFormData.username || ''} 
                onChange={handleAddFormChange} 
                required 
                className={formErrors.username ? 'error' : ''}
                style={getInputStyle('username')}
              />
              {formErrors.username && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.username}</span>}
            </div>
            <div className="detail-field">
              <label>Email *</label>
              <input 
                type="email" 
                name="email" 
                value={addFormData.email || ''} 
                onChange={handleAddFormChange} 
                required 
                className={formErrors.email ? 'error' : ''}
                style={getInputStyle('email')}
              />
              {formErrors.email && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.email}</span>}
            </div>
            <div className="detail-field">
              <label>Password *</label>
              <input 
                type="password" 
                name="password" 
                value={addFormData.password || ''} 
                onChange={handleAddFormChange} 
                required 
                className={formErrors.password ? 'error' : ''}
                style={getInputStyle('password')}
              />
              {formErrors.password && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.password}</span>}
            </div>
            <div className="detail-field">
              <label>First Name *</label>
              <input 
                type="text" 
                name="first_name" 
                value={addFormData.first_name || ''} 
                onChange={handleAddFormChange} 
                required 
                className={formErrors.first_name ? 'error' : ''}
                style={getInputStyle('first_name')}
              />
              {formErrors.first_name && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.first_name}</span>}
            </div>
            <div className="detail-field">
              <label>Last Name *</label>
              <input 
                type="text" 
                name="last_name" 
                value={addFormData.last_name || ''} 
                onChange={handleAddFormChange} 
                required 
                className={formErrors.last_name ? 'error' : ''}
                style={getInputStyle('last_name')}
              />
              {formErrors.last_name && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.last_name}</span>}
            </div>
            <div className="detail-field">
              <label>Date of Birth *</label>
              <input 
                type="date" 
                name="date_of_birth" 
                value={addFormData.date_of_birth || ''} 
                onChange={handleAddFormChange} 
                required 
                className={formErrors.date_of_birth ? 'error' : ''}
                style={getInputStyle('date_of_birth')}
              />
              {formErrors.date_of_birth && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.date_of_birth}</span>}
            </div>
            <div className="detail-field">
              <label>Gender</label>
              <select name="gender" value={addFormData.gender || ''} onChange={handleAddFormChange}>
                <option value="">Select...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div className="detail-field">
              <label>Position</label>
              <input type="text" name="position" value={addFormData.position || ''} onChange={handleAddFormChange} />
            </div>
            <div className="detail-field">
              <label>Footed</label>
              <select name="footed" value={addFormData.footed || ''} onChange={handleAddFormChange}>
                <option value="">Select...</option>
                <option value="Left">Left</option>
                <option value="Right">Right</option>
                <option value="Both">Both</option>
              </select>
            </div>
            <div className="detail-field">
              <label>Parent Contact Name</label>
              <input type="text" name="parent_contact_name" value={addFormData.parent_contact_name || ''} onChange={handleAddFormChange} />
            </div>
            <div className="detail-field">
              <label>Parent WhatsApp Phone *</label>
              <input 
                type="tel" 
                name="parent_whatsapp_phone" 
                value={addFormData.parent_whatsapp_phone || ''} 
                onChange={handleAddFormChange} 
                required 
                className={formErrors.parent_whatsapp_phone ? 'error' : ''}
                style={getInputStyle('parent_whatsapp_phone')}
              />
              {formErrors.parent_whatsapp_phone && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.parent_whatsapp_phone}</span>}
            </div>
            <div className="detail-field">
              <label>Player WhatsApp Phone</label>
              <input 
                type="tel" 
                name="player_whatsapp_phone" 
                value={addFormData.player_whatsapp_phone || ''} 
                onChange={handleAddFormChange} 
                className={formErrors.player_whatsapp_phone ? 'error' : ''}
                style={getInputStyle('player_whatsapp_phone')}
              />
              {formErrors.player_whatsapp_phone && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.player_whatsapp_phone}</span>}
            </div>
            <div className="detail-field">
              <label>Address *</label>
              <input 
                type="text" 
                name="address" 
                value={addFormData.address || ''} 
                onChange={handleAddFormChange} 
                required 
                className={formErrors.address ? 'error' : ''}
                style={getInputStyle('address')}
              />
              {formErrors.address && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.address}</span>}
            </div>
            <div className="detail-field">
              <label>Parent Email *</label>
              <input 
                type="email" 
                name="parent_email" 
                value={addFormData.parent_email || ''} 
                onChange={handleAddFormChange} 
                required 
                className={formErrors.parent_email ? 'error' : ''}
                style={getInputStyle('parent_email')}
              />
              {formErrors.parent_email && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.parent_email}</span>}
            </div>
            <div className="detail-field">
              <label>Player Email</label>
              <input type="email" name="player_email" value={addFormData.player_email || addFormData.email || ''} onChange={handleAddFormChange} />
            </div>
            <div className="detail-field">
              <label>Previous Club</label>
              <input type="text" name="previous_club" value={addFormData.previous_club || ''} onChange={handleAddFormChange} />
            </div>
            <div className="detail-field">
              <label>SAFA ID</label>
              <input type="text" name="safa_id" value={addFormData.safa_id || ''} onChange={handleAddFormChange} />
            </div>
            <div className="detail-field">
              <label>SA ID Number</label>
              <input type="text" name="sa_id_number" value={addFormData.sa_id_number || ''} onChange={handleAddFormChange} />
            </div>
            <div className="detail-field">
              <label>Team *</label>
              <select 
                name="team" 
                value={addFormData.team || ''} 
                onChange={handleAddFormChange}
                required
                className={formErrors.team ? 'error' : ''}
                style={getInputStyle('team')}
              >
                <option value="">Select Team... *</option>
                {TEAM_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {formErrors.team && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.team}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
            <button onClick={() => handleSubmitAdd('players')} className="save-btn" disabled={loading}>
              {loading ? 'Registering...' : 'Register Player'}
            </button>
            <button onClick={handleCancelAdd} className="cancel-btn">Cancel</button>
          </div>
        </div>
      );
    } else if (type === 'parents') {
      return (
        <div className="add-form" style={{ background: '#1f2230', padding: '2rem', borderRadius: '8px', border: '1px solid #383b48' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#567ad8' }}>Add New Parent</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div className="detail-field">
              <label>Username *</label>
              <input 
                type="text" 
                name="username" 
                value={addFormData.username || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('username')}
                style={getInputStyle('username')}
              />
              {formErrors.username && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.username}</span>}
            </div>
            <div className="detail-field">
              <label>Email *</label>
              <input 
                type="email" 
                name="email" 
                value={addFormData.email || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('email')}
                style={getInputStyle('email')}
              />
              {formErrors.email && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.email}</span>}
            </div>
            <div className="detail-field">
              <label>Password *</label>
              <input 
                type="password" 
                name="password" 
                value={addFormData.password || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('password')}
                style={getInputStyle('password')}
              />
              {formErrors.password && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.password}</span>}
            </div>
            <div className="detail-field">
              <label>First Name *</label>
              <input 
                type="text" 
                name="first_name" 
                value={addFormData.first_name || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('first_name')}
                style={getInputStyle('first_name')}
              />
              {formErrors.first_name && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.first_name}</span>}
            </div>
            <div className="detail-field">
              <label>Last Name *</label>
              <input 
                type="text" 
                name="last_name" 
                value={addFormData.last_name || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('last_name')}
                style={getInputStyle('last_name')}
              />
              {formErrors.last_name && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.last_name}</span>}
            </div>
            <div className="detail-field">
              <label>WhatsApp Phone *</label>
              <input 
                type="tel" 
                name="whatsapp_phone" 
                value={addFormData.whatsapp_phone || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('whatsapp_phone')}
                style={getInputStyle('whatsapp_phone')}
              />
              {formErrors.whatsapp_phone && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.whatsapp_phone}</span>}
            </div>
            <div className="detail-field">
              <label>Physical Address *</label>
              <input 
                type="text" 
                name="physical_address" 
                value={addFormData.physical_address || ''} 
                onChange={handleAddFormChange} 
                required 
                className={getInputClassName('physical_address')}
                style={getInputStyle('physical_address')}
              />
              {formErrors.physical_address && <span style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{formErrors.physical_address}</span>}
            </div>
            <div className="detail-field">
              <label>Number of Children</label>
              <input type="number" name="number_of_children" value={addFormData.number_of_children || 1} onChange={handleAddFormChange} min="1" />
            </div>
            <div className="detail-field" style={{ gridColumn: '1 / -1' }}>
              <label>Player Names (comma-separated)</label>
              <input type="text" name="player_names" value={addFormData.player_names || ''} onChange={handleAddFormChange} placeholder="e.g., John SMITH, Jane SMITH" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
            <button onClick={() => handleSubmitAdd('parents')} className="save-btn" disabled={loading}>
              {loading ? 'Registering...' : 'Register Parent'}
            </button>
            <button onClick={handleCancelAdd} className="cancel-btn">Cancel</button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderUserList = (users: User[], type: 'coaches' | 'players' | 'parents') => {
    
    const hasSelections = selectedUserIds.size > 0;
    const allSelected = users.length > 0 && selectedUserIds.size === users.length;
    
    return (
      <div className="user-list">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>{type.charAt(0).toUpperCase() + type.slice(1)}</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {hasSelections && (
              <button 
                onClick={() => handleDeleteSelected(type)}
                className="button danger"
                style={{ padding: '0.5rem 1rem' }}
              >
                Delete Selected ({selectedUserIds.size})
              </button>
            )}
            <button 
              onClick={handleAddUser}
              className="button primary"
              style={{ padding: '0.5rem 1rem' }}
            >
              + ADD
            </button>
          </div>
        </div>
        {(type === 'players' || type === 'parents') && (
          <div style={{ 
            fontSize: '0.8rem', 
            color: '#797b84', 
            marginBottom: '1rem', 
            padding: '0.5rem', 
            background: '#1f2230', 
            borderRadius: '4px',
            border: '1px solid #383b48'
          }}>
            <div>API URL: {buildApiUrl('')}</div>
            <div>{type.charAt(0).toUpperCase() + type.slice(1)} loaded: {users.length}</div>
            <div>Token exists: {getToken() ? 'Yes' : 'No'}</div>
            {users.length > 0 && (
              <div>Sample {type.slice(0, -1)}: {users[0].name || `${users[0].first_name || ''} ${users[0].last_name || ''}`.trim() || `ID: ${users[0].id}`}</div>
            )}
            <button 
              onClick={type === 'players' ? loadPlayers : loadParents} 
              className="button primary" 
              style={{ marginTop: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
            >
              Refresh {type.charAt(0).toUpperCase() + type.slice(1)} List
            </button>
          </div>
        )}
        {isAdding ? renderAddForm(type) : (
          users.length === 0 ? (
            <div>
              <p>No {type} found</p>
              {type === 'players' && (
                <button onClick={loadPlayers} className="button primary" style={{ marginTop: '1rem' }}>
                  Retry Loading Players
                </button>
              )}
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '0.5rem', padding: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => handleSelectAll(users)}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <label style={{ cursor: 'pointer', color: '#797b84', fontSize: '0.9rem' }}>
                  {allSelected ? 'Deselect All' : 'Select All'}
                </label>
              </div>
              <ul>
                {users.map((user) => (
                  <li key={user.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', marginBottom: '0.25rem', borderRadius: '4px', backgroundColor: '#1a1b26', border: '1px solid #383b48' }}>
                    <input
                      type="checkbox"
                      checked={selectedUserIds.has(user.id)}
                      onChange={() => handleToggleUserSelection(user.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer', width: '18px', height: '18px', flexShrink: 0 }}
                    />
                    <span 
                      onClick={() => handleUserClick(user.id, type)} 
                      style={{ cursor: 'pointer', flex: 1, color: '#e0e0e0' }}
                    >
                      {user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || `User #${user.id}`}
                    </span>
                    {type === 'players' && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleUserClick(user.id, type);
                          setViewingPlayerDashboard(true);
                        }}
                        className="button primary"
                        style={{ 
                          padding: '0.4rem 0.8rem', 
                          fontSize: '0.875rem',
                          marginLeft: '0.5rem'
                        }}
                      >
                        Dashboard
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )
        )}
      </div>
    );
  };

  const handleViewPlayerDashboard = () => {
    if (selectedUser && currentSection === 'players') {
      setViewingPlayerDashboard(true);
    }
  };

  const handleBackFromDashboard = () => {
    setViewingPlayerDashboard(false);
  };

  const renderUserDetails = (user: User, type: 'coaches' | 'players' | 'parents') => {
    if (!user) return null;

    // If viewing player dashboard, show the dashboard component
    if (type === 'players' && viewingPlayerDashboard) {
      return (
        <div>
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={handleBackFromDashboard} className="back-btn" style={{ padding: '0.5rem 1rem' }}>
              ← Back to Details
            </button>
            <h2 style={{ margin: 0, color: '#567ad8' }}>{user.first_name} {user.last_name} - Dashboard</h2>
          </div>
          <PlayerDashboard 
            playerId={user.id}
            onBackToCoachDashboard={handleBackFromDashboard}
            viewedByCoach={true}
          />
        </div>
      );
    }

    const fields = type === 'coaches' ? [
      'username', 'email', 'password', 'first_name', 'last_name', 'address',
      'whatsapp_phone', 'teams', 'licenses_certifications', 'sa_id_number',
      'safa_number', 'date_of_birth', 'is_admin'
    ] : type === 'players' ? [
      'username', 'email', 'password', 'first_name', 'last_name', 'date_of_birth',
      'position', 'footed', 'previous_club', 'safa_id', 'sa_id_number',
      'player_whatsapp_phone', 'address', 'player_email', 'club', 'team', 'gender', 'player_group'
    ] : [
      'username', 'email', 'password', 'first_name', 'last_name',
      'whatsapp_phone', 'physical_address', 'number_of_children'
    ];

    return (
      <div className="user-details">
        <h2>{user.first_name} {user.last_name} - Details</h2>
        <div className="details-content">
          {fields.map((field) => {
            // Special handling for club and team fields for players
            if (type === 'players' && field === 'club') {
              return (
                <div key={field} className="detail-field">
                  <label>{field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData[field] || 'Shosholoza FC'}
                      onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                      readOnly
                      style={{ backgroundColor: '#2a2a2a', cursor: 'not-allowed', opacity: 0.7 }}
                    />
                  ) : (
                    <span>{user[field] || 'Shosholoza FC'}</span>
                  )}
                </div>
              );
            }
            
            if (type === 'players' && field === 'team') {
              return (
                <div key={field} className="detail-field">
                  <label>{field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} *:</label>
                  {isEditing ? (
                    <select
                      value={editData[field] || ''}
                      onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                      required
                      style={{
                        padding: '0.75rem',
                        borderRadius: '5px',
                        width: '100%',
                        fontSize: '1rem',
                        outline: 'none',
                        border: editData[field] ? '1px solid #383b48' : '1px solid #ef4444',
                        backgroundColor: '#11121b',
                        color: '#fdfeff'
                      }}
                    >
                      <option value="">Select Team... *</option>
                      {TEAM_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ color: user[field] ? '#e0e0e0' : '#ef4444' }}>
                      {user[field] || '⚠️ REQUIRED - Team not assigned'}
                    </span>
                  )}
                </div>
              );
            }
            
            if (type === 'players' && field === 'gender') {
              return (
                <div key={field} className="detail-field">
                  <label>{field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</label>
                  {isEditing ? (
                    <select
                      value={editData[field] || ''}
                      onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        borderRadius: '5px',
                        width: '100%',
                        fontSize: '1rem',
                        outline: 'none',
                        border: '1px solid #383b48',
                        backgroundColor: '#11121b',
                        color: '#fdfeff'
                      }}
                    >
                      <option value="">Select Gender...</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  ) : (
                    <span>{user[field] || 'N/A'}</span>
                  )}
                </div>
              );
            }
            
            if (type === 'players' && field === 'player_group') {
              return (
                <div key={field} className="detail-field">
                  <label>Group:</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData[field] || ''}
                      onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                      placeholder="Enter group name"
                    />
                  ) : (
                    <span>{user[field] || 'N/A'}</span>
                  )}
                </div>
              );
            }
            
            // Special handling for teams field for coaches
            if (type === 'coaches' && field === 'teams') {
              
              // Parse current teams - handle both JSON string and comma-separated string
              const parseTeams = (teamsValue: any): string[] => {
                if (!teamsValue) return [];
                if (typeof teamsValue === 'string') {
                  // Try to parse as JSON first
                  try {
                    const parsed = JSON.parse(teamsValue);
                    if (Array.isArray(parsed)) {
                      return parsed;
                    }
                  } catch {
                    // Not JSON, treat as comma-separated
                  }
                  // Treat as comma-separated string
                  return teamsValue.split(',').map((t: string) => t.trim()).filter((t: string) => t);
                }
                if (Array.isArray(teamsValue)) {
                  return teamsValue;
                }
                return [];
              };
              
              const currentTeams = isEditing 
                ? parseTeams(editData[field])
                : parseTeams(user[field]);
              
              return (
                <div key={field} className="detail-field">
                  <label>Teams:</label>
                  {isEditing ? (
                    <select
                      multiple
                      value={currentTeams}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setEditData({ ...editData, [field]: selected.join(', ') });
                      }}
                      style={{
                        padding: '0.75rem',
                        borderRadius: '5px',
                        width: '100%',
                        minHeight: '120px',
                        fontSize: '1rem',
                        outline: 'none',
                        border: '1px solid #383b48',
                        backgroundColor: '#11121b',
                        color: '#fdfeff'
                      }}
                      size={Math.min(TEAM_OPTIONS.length, 8)}
                    >
                      {TEAM_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <span>{user[field] || 'N/A'}</span>
                  )}
                  {isEditing && (
                    <span style={{ 
                      color: '#797b84', 
                      fontSize: '0.875rem', 
                      marginTop: '0.5rem', 
                      display: 'block' 
                    }}>
                      Hold Ctrl (Cmd on Mac) to select multiple teams
                    </span>
                  )}
                </div>
              );
            }
            
            // Default field rendering
            return (
              <div key={field} className="detail-field">
                <label>{field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</label>
                {isEditing ? (
                  <input
                    type={field === 'password' ? 'password' : field === 'email' || field.includes('email') ? 'email' : 'text'}
                    value={editData[field] || ''}
                    onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                    placeholder={field === 'password' ? 'Enter new password (leave empty to keep current)' : ''}
                  />
                ) : (
                  <span>{field === 'password' ? '••••••••' : (user[field] !== null && user[field] !== undefined ? String(user[field]) : 'N/A')}</span>
                )}
              </div>
            );
          })}
          
          {/* Display linked parent for players */}
          {type === 'players' && (
            <div className="detail-field" style={{ gridColumn: '1 / -1', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #383b48' }}>
              <label style={{ fontWeight: '600', color: '#567ad8', marginBottom: '0.5rem', display: 'block' }}>Linked Parent:</label>
              {user.linked_parent ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setCurrentSection('parents');
                      handleUserClick(user.linked_parent.id, 'parents');
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#567ad8',
                      fontWeight: '500',
                      cursor: 'pointer',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      fontSize: '1rem',
                      transition: 'all 0.2s ease',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(86, 122, 216, 0.1)';
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    {user.linked_parent.full_name}
                  </button>
                  {user.linked_parent.relationship && user.linked_parent.relationship !== 'Parent' && (
                    <span style={{ color: '#797b84', fontSize: '0.9rem' }}>
                      ({user.linked_parent.relationship})
                    </span>
                  )}
                  <span style={{ color: '#797b84', fontSize: '0.85rem', fontStyle: 'italic', marginLeft: 'auto' }}>
                    Edit from Parent Detail page
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: '#797b84', fontStyle: 'italic' }}>No parent linked</span>
                  <span style={{ color: '#797b84', fontSize: '0.85rem', fontStyle: 'italic', marginLeft: 'auto' }}>
                    Link from Parent Detail page
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Display and manage linked children for parents */}
          {type === 'parents' && (
            <div className="detail-field" style={{ gridColumn: '1 / -1', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #383b48' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontWeight: '600', color: '#567ad8', display: 'block' }}>Linked Children:</label>
                {isEditing && (
                  <button
                    onClick={() => {
                      setAddingChild(true);
                      setNewChildData({ relationship_type: 'Child' });
                    }}
                    className="button primary"
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                  >
                    + Add Child
                  </button>
                )}
              </div>
              
              {user.children && Array.isArray(user.children) && user.children.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {user.children.map((child: any, index: number) => (
                    <div key={index} style={{ 
                      padding: '0.75rem', 
                      backgroundColor: '#1a1b26', 
                      borderRadius: '5px', 
                      border: '1px solid #383b48',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                        {isEditing ? (
                          <>
                            <select
                              value={child.player_id || ''}
                              onChange={(e) => {
                                const selectedPlayerId = e.target.value ? parseInt(e.target.value) : null;
                                const selectedPlayer = allPlayers.find(p => p.id === selectedPlayerId);
                                const updates: any = { player_id: selectedPlayerId };
                                if (selectedPlayer) {
                                  updates.player_full_name = `${selectedPlayer.first_name} ${selectedPlayer.last_name}`;
                                } else if (!selectedPlayerId) {
                                  updates.player_full_name = child.player_name || '';
                                }
                                handleUpdateChild(user.id, child, updates);
                              }}
                              style={{
                                padding: '0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #383b48',
                                backgroundColor: '#11121b',
                                color: '#fdfeff',
                                minWidth: '200px'
                              }}
                            >
                              <option value="">-- Select Player or Enter Name --</option>
                              {allPlayers.map((player) => (
                                <option key={player.id} value={player.id}>
                                  {player.first_name} {player.last_name}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={child.player_id ? allPlayers.find(p => p.id === child.player_id)?.first_name + ' ' + allPlayers.find(p => p.id === child.player_id)?.last_name || child.player_name : (child.player_name || '')}
                              onChange={(e) => {
                                if (!child.player_id) {
                                  handleUpdateChild(user.id, child, { player_full_name: e.target.value });
                                }
                              }}
                              placeholder="Or enter child name..."
                              disabled={!!child.player_id}
                              style={{
                                padding: '0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #383b48',
                                backgroundColor: child.player_id ? '#2a2a2a' : '#11121b',
                                color: '#fdfeff',
                                flex: 1,
                                opacity: child.player_id ? 0.5 : 1
                              }}
                            />
                            <input
                              type="text"
                              value={child.relationship || 'Child'}
                              onChange={(e) => handleUpdateChild(user.id, child, { relationship_type: e.target.value })}
                              placeholder="Relationship"
                              style={{
                                padding: '0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #383b48',
                                backgroundColor: '#11121b',
                                color: '#fdfeff',
                                width: '120px'
                              }}
                            />
                            <button
                              onClick={() => handleDeleteChild(user.id, child)}
                              className="button danger"
                              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                            >
                              Remove
                            </button>
                          </>
                        ) : (
                          <>
                            {child.player_id ? (
                              <button
                                onClick={() => {
                                  setCurrentSection('players');
                                  handleUserClick(child.player_id, 'players');
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#567ad8',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  textAlign: 'left',
                                  fontSize: '1rem',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(86, 122, 216, 0.1)';
                                  e.currentTarget.style.textDecoration = 'underline';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.textDecoration = 'none';
                                }}
                              >
                                {child.player_name}
                              </button>
                            ) : (
                              <span style={{ color: '#e0e0e0', fontWeight: '500' }}>
                                {child.player_name}
                              </span>
                            )}
                            {child.relationship && child.relationship !== 'Child' && (
                              <span style={{ color: '#797b84', fontSize: '0.9rem' }}>
                                ({child.relationship})
                              </span>
                            )}
                            {!child.player_id && (
                              <span style={{ color: '#797b84', fontSize: '0.85rem', fontStyle: 'italic', marginLeft: 'auto' }}>
                                Not yet registered
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <span style={{ color: '#797b84', fontStyle: 'italic' }}>No children linked to this parent</span>
              )}

              {/* Add new child form */}
              {isEditing && addingChild && (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '1rem', 
                  backgroundColor: '#1a1b26', 
                  borderRadius: '5px', 
                  border: '2px solid #567ad8',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                      value={newChildData.player_id || ''}
                      onChange={(e) => {
                        const playerId = e.target.value ? parseInt(e.target.value) : null;
                        setNewChildData({ 
                          ...newChildData, 
                          player_id: playerId,
                          player_full_name: playerId ? '' : newChildData.player_full_name // Clear manual name if player selected
                        });
                      }}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #383b48',
                        backgroundColor: '#11121b',
                        color: '#fdfeff',
                        minWidth: '200px'
                      }}
                    >
                      <option value="">-- Select Existing Player --</option>
                      {allPlayers.map((player) => (
                        <option key={player.id} value={player.id}>
                          {player.first_name} {player.last_name}
                        </option>
                      ))}
                    </select>
                    <span style={{ color: '#797b84' }}>OR</span>
                    <input
                      type="text"
                      value={newChildData.player_full_name || ''}
                      onChange={(e) => {
                        setNewChildData({ 
                          ...newChildData, 
                          player_full_name: e.target.value,
                          player_id: null // Clear player selection if typing manually
                        });
                      }}
                      placeholder="Enter child name..."
                      disabled={!!newChildData.player_id}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #383b48',
                        backgroundColor: newChildData.player_id ? '#2a2a2a' : '#11121b',
                        color: '#fdfeff',
                        flex: 1,
                        minWidth: '200px',
                        opacity: newChildData.player_id ? 0.5 : 1
                      }}
                    />
                    <input
                      type="text"
                      value={newChildData.relationship_type || 'Child'}
                      onChange={(e) => setNewChildData({ ...newChildData, relationship_type: e.target.value })}
                      placeholder="Relationship"
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #383b48',
                        backgroundColor: '#11121b',
                        color: '#fdfeff',
                        width: '120px'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        console.log('🔘 Add Child button clicked');
                        handleAddChild(e);
                      }}
                      disabled={!newChildData.player_id && !newChildData.player_full_name?.trim()}
                      className="button primary"
                      style={{ 
                        fontSize: '0.875rem', 
                        padding: '0.5rem 1rem',
                        opacity: (!newChildData.player_id && !newChildData.player_full_name?.trim()) ? 0.5 : 1,
                        cursor: (!newChildData.player_id && !newChildData.player_full_name?.trim()) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Add Child
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        console.log('🔘 Cancel button clicked');
                        setAddingChild(false);
                        setNewChildData({ relationship_type: 'Child' });
                      }}
                      className="button"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', backgroundColor: '#383b48' }}
                    >
                      Cancel
                    </button>
                    {(!newChildData.player_id && !newChildData.player_full_name?.trim()) && (
                      <span style={{ color: '#797b84', fontSize: '0.875rem', fontStyle: 'italic' }}>
                        Select a player or enter a name to add
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="details-actions">
          {isEditing ? (
            <>
              <button onClick={handleSave} className="save-btn">Save</button>
              <button onClick={() => { setIsEditing(false); setEditData({ ...user }); }} className="cancel-btn">Cancel</button>
            </>
          ) : (
            <>
              <button onClick={handleEdit} className="edit-btn">Edit</button>
              {type === 'players' && playerHasEvaluation && (
                <button onClick={handleViewPlayerDashboard} className="button primary" style={{ marginLeft: '0.5rem' }}>
                  View Dashboard
                </button>
              )}
              <button onClick={handleExit} className="exit-btn">Exit</button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-page">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <button onClick={onBackToLanding} className="back-btn" style={{ color: '#ff0000', borderColor: '#ff0000' }}>LOGOUT</button>
        </div>
        <div className="admin-content">
        <div className="admin-sidebar">
          <button
            onClick={() => { setCurrentSection('coaches'); setSelectedUser(null); setIsAdding(false); }}
            className={currentSection === 'coaches' ? 'active' : ''}
          >
            Coaches
          </button>
          <button
            onClick={() => { setCurrentSection('players'); setSelectedUser(null); setIsAdding(false); }}
            className={currentSection === 'players' ? 'active' : ''}
          >
            Players
          </button>
          <button
            onClick={() => { setCurrentSection('parents'); setSelectedUser(null); setIsAdding(false); }}
            className={currentSection === 'parents' ? 'active' : ''}
          >
            Parents
          </button>
          <button
            onClick={() => { setCurrentSection('admins'); setSelectedUser(null); }}
            className={currentSection === 'admins' ? 'active' : ''}
          >
            Admins
          </button>
          <button
            onClick={() => { setCurrentSection('api'); setSelectedUser(null); }}
            className={currentSection === 'api' ? 'active' : ''}
          >
            API Management
          </button>
          <button
            onClick={() => { setCurrentSection('queries'); setSelectedUser(null); }}
            className={currentSection === 'queries' ? 'active' : ''}
          >
            Performance Queries
          </button>
          <button
            onClick={() => { setCurrentSection('weights'); setSelectedUser(null); }}
            className={currentSection === 'weights' ? 'active' : ''}
          >
            Manage Indicator Weights
          </button>
          <button
            onClick={() => { 
              setCurrentSection('search'); 
              setSelectedUser(null); 
              setIsAdding(false);
              setSearchQuery('');
              setSearchResults([]);
            }}
            className={currentSection === 'search' ? 'active' : ''}
            style={{ marginTop: 'auto' }}
          >
            🔍 Search
          </button>
        </div>
        <div className="admin-main">
          {currentSection === 'coaches' && (
            selectedUser ? renderUserDetails(selectedUser, 'coaches') : renderUserList(coaches, 'coaches')
          )}
          {currentSection === 'players' && (
            selectedUser ? renderUserDetails(selectedUser, 'players') : renderUserList(players, 'players')
          )}
          {currentSection === 'parents' && (
            selectedUser ? renderUserDetails(selectedUser, 'parents') : renderUserList(parents, 'parents')
          )}
          {currentSection === 'admins' && (
            renderAdminsList()
          )}
          {currentSection === 'api' && (
            <div className="api-management">
              <h2>API Management</h2>
              <div className="form-group">
                <label>OpenAI API Key</label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter API key"
                />
              </div>
              <div className="form-group">
                <label className="toggle-label">
                  <span>API Key Status:</span>
                  <span style={{ marginLeft: '1rem', color: apiKeyActive ? '#4ade80' : '#ef4444' }}>
                    {apiKeyActive ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleSaveApiKey} className="save-btn">Save API Key</button>
                <button onClick={handleToggleApiKey} className="button primary">
                  {apiKeyActive ? 'Disable' : 'Enable'} API Key
                </button>
              </div>
            </div>
          )}
          {currentSection === 'queries' && (
            <PerformanceQueriesSection 
              onBack={() => {
                setCurrentSection('coaches');
                setSelectedUser(null);
              }} 
            />
          )}
          {currentSection === 'weights' && (
            <IndicatorWeightsSection 
              onBack={() => {
                setCurrentSection('coaches');
                setSelectedUser(null);
              }} 
            />
          )}
          {currentSection === 'search' && (
            <div className="search-section" style={{ padding: '2rem' }}>
              <h2>Search All Users</h2>
              <p style={{ marginBottom: '1.5rem', color: '#9ca3af' }}>
                Search across players, coaches, and parents by name, username, or email
              </p>
              
              <div className="admin-search-box" style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAdminSearch();
                    }
                  }}
                  placeholder="Enter name, username, or email..."
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #383b48',
                    backgroundColor: '#11121b',
                    color: '#fdfeff',
                    fontSize: '1rem'
                  }}
                />
                <button
                  onClick={handleAdminSearch}
                  disabled={searchLoading || !searchQuery.trim() || searchQuery.length < 2}
                  className="button primary"
                  style={{ minWidth: '120px' }}
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {searchResults.length > 0 && (
                <div>
                  <h3 style={{ marginBottom: '1rem', color: '#e0e0e0' }}>
                    Results ({searchResults.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {searchResults.map((result, index) => (
                      <div
                        key={`${result.type}-${result.id}`}
                        onClick={() => {
                          const typeMap: { [key: string]: 'coaches' | 'players' | 'parents' } = {
                            'coach': 'coaches',
                            'player': 'players',
                            'parent': 'parents'
                          };
                          setCurrentSection(typeMap[result.type]);
                          handleUserClick(result.id, typeMap[result.type]);
                        }}
                        style={{
                          padding: '1rem',
                          borderRadius: '6px',
                          border: '1px solid #383b48',
                          backgroundColor: '#1a1b23',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#252630';
                          e.currentTarget.style.borderColor = '#567ad8';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#1a1b23';
                          e.currentTarget.style.borderColor = '#383b48';
                        }}
                      >
                        <div
                          style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            backgroundColor:
                              result.type === 'coach' ? '#567ad8' :
                              result.type === 'player' ? '#4ade80' :
                              '#f59e0b',
                            color: '#fff',
                            minWidth: '80px',
                            textAlign: 'center'
                          }}
                        >
                          {result.type}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#fdfeff' }}>
                            {result.first_name} {result.last_name}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                            {result.username && `@${result.username}`}
                            {result.email && ` • ${result.email}`}
                            {!result.is_active && ' • Inactive'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {searchQuery && searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                  No results found for "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
